<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import CopyTrackWrapper from '@/components/CopyTrackWrapper.vue'
import GbBreadcrumbs from '@/components/GbBreadcrumbs.vue'
import NoAccessError from '@/components/NoAccessError.vue'
import PremiumActionModal from '@/components/PremiumActionModal.vue'
import { useUserFeedbackContext } from '@/components/useUserFeedbackContext'
import ContentReviewButton from '@/content-review/components/ContentReviewButton.vue'
import ContentReviewModal from '@/content-review/components/ContentReviewModal.vue'
import ContentReviewResults from '@/content-review/components/ContentReviewResults.vue'
import { useContentReviewStore } from '@/content-review/content-review.store'
import type { AuditResult } from '@/content-review/api'
import { useContentProfileStore } from '@/contentprofile/contentprofile.store'
import { getLibraryGoal } from '@/library/api'
import { useLibraryStore } from '@/library/library.store'
import type { CopyGoalProps, LibraryFolder, LibraryGoal } from '@/library/library_types'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin'
import { CONTENT_TYPES } from '@/util/access_visibility'
import { setTitle } from '@/util/title'
import { VUE2_CONTAINER_WIDTHS } from '@/util/vue2_layout'
import { useVisibilityStore } from '@/visibility/visibility.store'
import AccessPointLabel from './AccessPointLabel.vue'
import AnchorPageLabel from './AnchorPageLabel.vue'
import PrintableSavedAnchorPage from './PrintableSavedAnchorPage.vue'
import AnchorAccordionObjectives from './anchor-page/AnchorAccordionObjectives.vue'
import AnchorAnnualGoal from './anchor-page/AnchorAnnualGoal.vue'
import AnchorPageTitle from './anchor-page/AnchorPageTitle.vue'
import AnchorQuarterlyObjectives from './anchor-page/AnchorQuarterlyObjectives.vue'
import AnnualTargetText from './anchor-page/AnnualTargetText.vue'
import { useAnchorPageStore } from './anchor_page.store'
import type { AnchorObjective } from './anchor_page_types'
import { ANCHOR_PAGE_EVENT_NAMES } from './anchor_page_event_names'
import GenerateAssessmentModal from './assessments/GenerateAssessmentModal.vue'
import { ANNUAL_TARGET_LABEL, objectiveTargetLabel } from './assessments/generation_targets'
import { useGoalAutoReview, useGoalCriteriaSetName } from './auto-review/useGoalAutoReview'
import { COPY_TO_EDIT_HINT } from './goal_sections'
import { useGoalTranslationStore } from './goal_translation.store'
import { eachKeyComputed } from './page_event_context'
import SavedAnchorAssessmentTiles from './saved-anchor-page/SavedAnchorAssessmentTiles.vue'
import SavedAnchorPageSidebar from './saved-anchor-page/SavedAnchorPageSidebar.vue'
import { useSavedAnchorAssessments } from './saved-anchor-page/useSavedAnchorAssessments'
import { useSavedAnchorEditing } from './saved-anchor-page/useSavedAnchorEditing'
import { useSavedAnchorPrint } from './useSavedAnchorPrint'

const FORBIDDEN_STATUS = 403

const props = defineProps<{ uuid: string }>()

const anchorPageStore = useAnchorPageStore()
const {
  content,
  isFetching,
  customPrimaryGoalText,
  customObjectives,
  hasEditedText,
  hasAccordionObjectives,
  hasObjectives,
  isGoalCustomized,
  keyStandard,
  contentEventContext
} = storeToRefs(anchorPageStore)
const libraryStore = useLibraryStore()
const { canEditCurrentFolderContent, currentFolderUserRole, loadFolderError } = storeToRefs(libraryStore)
const { profileId } = storeToRefs(useContentProfileStore())
const { aiFeatureVisibility } = storeToRefs(useVisibilityStore())
const translationStore = useGoalTranslationStore()
const { updateContext: updateFeedbackContext } = useUserFeedbackContext()
const criteriaSetName = useGoalCriteriaSetName()
const contentReviewStore = useContentReviewStore()

const savedCopy = ref<LibraryGoal | null>(null)
const hasAccess = ref<boolean | undefined>(undefined)
const hasLoadError = ref(false)
const isCopyLoaded = ref(false)
const isLoaded = ref(false)
const loadedFolderUuid = ref('')

const studentName = computed(() => savedCopy.value?.student_name ?? '')
const studentUuid = computed(() => savedCopy.value?.student_uuid ?? '')

// Vue 2's eventContext keys, over the anchor page's own: a saved copy is library content.
const pageEventContext = computed(() => ({
  ...contentEventContext.value,
  goal_is_library: true,
  is_library: true,
  saved_goal_uuid: props.uuid,
  student_uuid: studentUuid.value
}))
const analytics = getAnalyticsTracker().getChildContext(eachKeyComputed(pageEventContext))

// Read-only until this copy's own folder has answered, so a previous folder's permissions never apply.
const isFolderLoaded = computed(
  () => Boolean(studentUuid.value) && loadedFolderUuid.value === studentUuid.value && libraryStore.currentFolder?.uuid === studentUuid.value
)
const isReadOnly = computed(() => !isFolderLoaded.value || loadFolderError.value || !canEditCurrentFolderContent.value)
const copySource = computed(() => ({ uuid: savedCopy.value?.uuid ?? '', title: savedCopy.value?.title ?? '', isGated: true }))
const goalLabel = computed(() => savedCopy.value?.title ?? '')
// Accordion pages name the editor after the goal, as Vue 2's contentTitle did.
const goalTextTitle = computed(() => (hasAccordionObjectives.value ? goalLabel.value : ANNUAL_TARGET_LABEL))

function showSavedCopy(loaded: LibraryGoal) {
  savedCopy.value = loaded
}

let latestLoad = 0

const { saveStatus, editVersion, save, discard, isLeavingUnsavedEditsConfirmed } = useSavedAnchorEditing(
  () => props.uuid,
  () => latestLoad,
  analytics,
  showSavedCopy
)

const assessments = useSavedAnchorAssessments(() => props.uuid, isCopyLoaded, analytics)
const { generation, isGenerateBlockedModalOpen } = assessments
const { isModalOpen: isGenerateModalOpen } = generation

const printTarget = computed(() => ({
  savedPageUuid: props.uuid,
  title: savedCopy.value?.title ?? '',
  sourceGoalUuid: savedCopy.value?.goal_uuid ?? ''
}))
const { printPermissions, isPrintViewOpen, isPrintBlockedModalOpen, openPrintView, closePrintView, trackPrint } =
  useSavedAnchorPrint(printTarget, analytics)

// Until the source anchor page arrives, the frame shows only the copy, so its own saved text is what copy and print take.
const shownGoalText = computed(() => customPrimaryGoalText.value ?? savedCopy.value?.grade_level_goal ?? '')
function withEditedTexts(objectives: AnchorObjective[]) {
  return objectives.map((objective, index) => ({
    level: objective.level,
    title: objective.title,
    text: customObjectives.value[index]?.text ?? objective.text
  }))
}
const editedObjectives = computed(() =>
  content.value ? withEditedTexts(content.value.objectives ?? []) : savedCopy.value?.objectives ?? []
)

const copyGoal = computed<CopyGoalProps>(() => ({
  sourceGoalUuid: savedCopy.value?.goal_uuid ?? '',
  gradeLevelGoal: shownGoalText.value,
  objectives: editedObjectives.value,
  isPersonalized: hasEditedText.value,
  sourceFolderUuid: studentUuid.value
}))

const breadcrumbs = computed(() => [
  { label: 'Library: All Folders', to: { name: 'AllFolders' } },
  ...(studentUuid.value
    ? [{ label: studentName.value, icon: 'folder', to: { name: 'LibraryFolder', params: { uuid: studentUuid.value } }, fsExclude: true }]
    : []),
  ...(savedCopy.value?.title ? [{ label: `Goal: ${savedCopy.value.title}` }] : [])
])

const goalSubjects = computed(() => (content.value?.subject_alignments ?? []).map((alignment) => alignment.subject))
const reviewedGoal = computed(() => ({
  uuid: content.value?.uuid ?? '',
  goalText: customPrimaryGoalText.value ?? '',
  objectiveTexts: customObjectives.value.map((objective) => objective.text),
  grades: content.value?.grades ?? [],
  subjects: goalSubjects.value,
  isCustomized: isGoalCustomized.value
}))
const {
  isModalOpen: isReviewModalOpen,
  isReviewAvailable,
  hasCompletedReview,
  criteriaByType,
  isCriterionVisible,
  reviewButtonText,
  reviewDescriptionLabel,
  goalReviewProps,
  objectivesReviewProps,
  isGoalReviewVisible,
  isObjectivesReviewVisible,
  openModal: openReviewModal,
  review,
  dismissGoalReview,
  dismissObjectivesReview,
  trackResultToggle
} = useGoalAutoReview({ goal: reviewedGoal, isSavedGoal: true, analytics })
const ANNUAL_GOAL_EVENT_CONTEXT = { obj_level: 'Annual' }
const trackAnnualResultToggle = (result: AuditResult) => trackResultToggle(result, ANNUAL_GOAL_EVENT_CONTEXT)
const isReviewButtonShown = computed(() => !isFetching.value && isReviewAvailable.value)

function reloadPage() {
  if (isLeavingUnsavedEditsConfirmed()) window.location.reload()
}

function recordRemoval() {
  analytics.sendEvent(ANCHOR_PAGE_EVENT_NAMES.CLICKED_MOVE_TO_TRASH, { uuid: props.uuid, type: CONTENT_TYPES.SAVED_SUPER_PAGE })
}

function isForbidden(error: unknown): boolean {
  return (error as { status_code?: number } | null)?.status_code === FORBIDDEN_STATUS
}

function clearContentFolder() {
  loadedFolderUuid.value = ''
  libraryStore.currentFolder = {} as LibraryFolder
  libraryStore.setPermissions([])
}

// Another copy shares nothing with the one being left, so none of its text, review or translation carries over.
function leaveCopy() {
  anchorPageStore.clearPage()
  clearContentFolder()
  savedCopy.value = null
  hasAccess.value = undefined
}

async function loadFolder(folderUuid: string, isSuperseded: () => boolean) {
  await libraryStore.fetchResourcesForFolder({ folderUUID: folderUuid, params: {} })
  if (!isSuperseded()) loadedFolderUuid.value = folderUuid
}

async function loadPage() {
  const thisLoad = ++latestLoad
  const isSuperseded = () => thisLoad !== latestLoad
  if (savedCopy.value && savedCopy.value.uuid !== props.uuid) leaveCopy()
  hasLoadError.value = false
  isCopyLoaded.value = false
  isLoaded.value = false
  let loaded: LibraryGoal
  try {
    loaded = await getLibraryGoal(props.uuid)
  } catch (error) {
    if (isSuperseded()) return
    if (isForbidden(error)) {
      hasAccess.value = false
      setTitle('Saved Anchor Page')
      return
    }
    console.error('loadPageContent', error)
    hasLoadError.value = true
    return
  }
  if (isSuperseded()) return
  hasAccess.value = true
  showSavedCopy(loaded)
  isCopyLoaded.value = true
  const folderLoad = loadFolder(loaded.student_uuid ?? '', isSuperseded)
  await anchorPageStore.fetchAnchorPage({ uuid: loaded.goal_uuid ?? '', isDraft: false, savedCopy: loaded })
  if (isSuperseded()) return
  setTitle(`${loaded.title} | Saved Anchor Page`)
  updateFeedbackContext({ uuid: loaded.uuid, title: loaded.title, subject: content.value?.subject })
  translationStore.fetchLanguages()
  isLoaded.value = true
  anchorPageStore.fetchAllResources()
  await folderLoad
  if (isSuperseded()) return
  analytics.sendEvent(ANCHOR_PAGE_EVENT_NAMES.VIEWED_SAVED_PAGE, { user_folder_role: currentFolderUserRole.value || undefined })
}

function reloadForProfile() {
  contentReviewStore.fetchProfiles()
  loadPage()
}

watch(() => props.uuid, loadPage, { immediate: true })
watch(profileId, reloadForProfile)

onBeforeUnmount(() => {
  latestLoad++
  anchorPageStore.clearPage()
  clearContentFolder()
})
</script>

<template>
  <CopyTrackWrapper type="saved_super_page" :title="savedCopy?.title" :uuid="savedCopy?.uuid" gate-copy>
    <div class="container pt-4 text-gray-900 print:hidden" :class="VUE2_CONTAINER_WIDTHS" data-saved-anchor-page data-testid="saved-anchor-page">
      <GbBreadcrumbs :items="breadcrumbs" class="mb-7 [&_ol]:rounded-none [&_ol]:py-2.5" />

      <NoAccessError v-if="hasAccess === false" />
      <p v-else-if="hasLoadError" data-testid="saved-anchor-load-error">There was an error loading the page. Please try again.</p>
      <div v-else-if="savedCopy" class="md:grid md:grid-cols-12 md:gap-x-8">
        <div class="md:col-span-8">
          <AnchorPageTitle :title="savedCopy.title" :goal-label="goalLabel" />
          <template v-if="content">
            <div class="flex flex-wrap items-baseline gap-1" :class="{ 'mb-2': hasAccordionObjectives }">
              <AnchorPageLabel v-if="!hasAccordionObjectives" />
              <AccessPointLabel v-if="content.access_point_level" :level="content.access_point_level" />
              <!-- Vue 2's label-success green (#5cb85c) gave white text 2.5:1, so this takes the success token, which passes AA -->
              <span class="rounded-xxs bg-success px-2 py-1 text-xs font-bold leading-none text-white" data-testid="saved-to-label">
                <span class="material-icons-round align-text-bottom text-xs leading-none" aria-hidden="true">person</span>
                Saved to <span class="fs-exclude">{{ studentName }}</span>
              </span>
            </div>

            <AnchorAnnualGoal
              :goal-label="goalLabel"
              :is-ready-to-translate="isLoaded"
              :is-generate-tease-shown="false"
              is-translated-as-edited
              :has-annual-assessments="assessments.hasAnnualAssessments.value"
            >
              <template #goalReview>
                <ContentReviewResults v-if="isGoalReviewVisible" v-bind="goalReviewProps" @toggle="trackAnnualResultToggle" @close="dismissGoalReview" />
              </template>
              <template #goalText="{ openTranslation, closeTranslation }">
                <AnnualTargetText
                  :key="editVersion"
                  :goal-label="goalTextTitle"
                  :is-accordion-layout="hasAccordionObjectives"
                  :is-translate-button-shown="aiFeatureVisibility"
                  :translation-languages="translationStore.languages"
                  :is-read-only="isReadOnly"
                  :read-only-hint="COPY_TO_EDIT_HINT"
                  :copy-source="copySource"
                  is-library-copy
                  @translate="openTranslation"
                  @reset="closeTranslation"
                />
              </template>
              <template #annualAssessments>
                <SavedAnchorAssessmentTiles
                  :assessments="assessments.annualAssessments.value"
                  :target-label="ANNUAL_TARGET_LABEL"
                  :is-generate-shown="assessments.isEntryPointShown.value"
                  :blocked-reason="assessments.blockedReason.value"
                  @generate="assessments.requestAnnualGeneration"
                  @deleted="assessments.removeAssessment"
                />
              </template>
              <template #objectivesReview>
                <ContentReviewResults
                  v-if="isObjectivesReviewVisible"
                  v-bind="objectivesReviewProps"
                  @toggle="trackAnnualResultToggle"
                  @close="dismissObjectivesReview"
                />
              </template>
              <template #accordionObjectives>
                <AnchorAccordionObjectives
                  :key="editVersion"
                  :is-read-only="isReadOnly"
                  :read-only-hint="COPY_TO_EDIT_HINT"
                  :levels-with-generated-assessments="assessments.levelsWithGeneratedAssessments.value"
                >
                  <template #generatedAssessments="{ objective, index }">
                    <SavedAnchorAssessmentTiles
                      :assessments="assessments.objectiveAssessments(objective.level)"
                      :target-label="objectiveTargetLabel(objective.level)"
                      :is-generate-shown="assessments.isEntryPointShown.value"
                      :blocked-reason="assessments.blockedReason.value"
                      @generate="assessments.requestObjectiveGeneration(objective, index)"
                      @deleted="assessments.removeAssessment"
                    />
                  </template>
                </AnchorAccordionObjectives>
              </template>
            </AnchorAnnualGoal>

            <AnchorQuarterlyObjectives
              v-if="!hasAccordionObjectives && hasObjectives"
              :key="editVersion"
              is-library-copy
              :is-read-only="isReadOnly"
              :read-only-hint="COPY_TO_EDIT_HINT"
              :levels-with-generated-assessments="assessments.levelsWithGeneratedAssessments.value"
            >
              <template #generatedAssessments="{ objective, index }">
                <SavedAnchorAssessmentTiles
                  :assessments="assessments.objectiveAssessments(objective.level)"
                  :target-label="objectiveTargetLabel(objective.level)"
                  :is-generate-shown="assessments.isEntryPointShown.value"
                  :blocked-reason="assessments.blockedReason.value"
                  @generate="assessments.requestObjectiveGeneration(objective, index)"
                  @deleted="assessments.removeAssessment"
                />
              </template>
            </AnchorQuarterlyObjectives>
          </template>
        </div>

        <div class="md:col-span-4">
          <SavedAnchorPageSidebar
            :saved-copy="savedCopy"
            :save-status="saveStatus"
            :copy-goal="copyGoal"
            @save="save"
            @discard="discard"
            @print="openPrintView"
            @remove-confirmed="recordRemoval"
          >
            <template #review>
              <ContentReviewButton
                v-if="isReviewButtonShown"
                class="mt-2.5"
                :criteria-set-name="criteriaSetName"
                :has-existing-results="hasCompletedReview"
                @click="openReviewModal"
              />
              <ContentReviewModal
                v-model:open="isReviewModalOpen"
                :criteria-by-type="criteriaByType"
                :criteria-set-name="criteriaSetName"
                :subjects="goalSubjects"
                :is-criterion-visible="isCriterionVisible"
                :review-button-text="reviewButtonText"
                :description-label="reviewDescriptionLabel"
                @review="review"
              />
            </template>
          </SavedAnchorPageSidebar>
        </div>
      </div>
    </div>

    <PrintableSavedAnchorPage
      v-if="isPrintViewOpen && savedCopy"
      :key-standard="keyStandard"
      :title="savedCopy.title"
      :annual-text="shownGoalText"
      :goal-example-text="content?.goal_example_text"
      :objectives="editedObjectives"
      :student-name="studentName"
      @close="closePrintView"
      @print="trackPrint"
    />
    <PremiumActionModal v-model:external-open="isPrintBlockedModalOpen" :action-permissions="printPermissions" />
    <PremiumActionModal v-model:external-open="isGenerateBlockedModalOpen" :action-permissions="anchorPageStore.actionPermissions.generateAssessment" />
    <GenerateAssessmentModal
      v-model:open="isGenerateModalOpen"
      :target-label="generation.target.value.targetLabel"
      :target-text="generation.target.value.targetText"
      :is-running="generation.isRunning.value"
      :stage="generation.stage.value"
      :tick="generation.tick.value"
      :detail="generation.detail.value"
      :result-uuid="generation.resultUuid.value"
      :error-message="generation.errorMessage.value"
      :error-kind="generation.errorKind.value"
      @generate="generation.startGeneration"
      @reload="reloadPage"
    />
  </CopyTrackWrapper>
</template>

<style>
/* Vue 2's saved anchor page printed a blank page from the browser's own print; only the preview prints anything. */
@media print {
  body:has([data-saved-anchor-page]):not(:has(> [data-print-overlay])) > * {
    display: none !important;
  }
}
</style>
